import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const particles = Array.from({ length: 20 }, (_, i) => ({
  id: i,
  size: Math.random() * 6 + 3,
  left: Math.random() * 100,
  delay: Math.random() * 8,
  duration: Math.random() * 6 + 8,
  color: ['#f97316', '#ec4899', '#f59e0b', '#ef4444', '#fb923c'][Math.floor(Math.random() * 5)],
}));

const slides = [
  {
    image: 'https://readdy.ai/api/search-image?query=Premium%20beauty%20skincare%20products%20with%20natural%20ingredients%20serums%20moisturizers%20and%20cosmetic%20bottles%20arranged%20elegantly%20on%20clean%20white%20surface%20with%20soft%20peachy%20coral%20lighting%20professional%20product%20photography%20showcasing%20healthy%20glowing%20skin%20care%20routine&width=1920&height=1080&seq=hero-beauty-slide1&orientation=landscape',
    title: 'Healthy Skin',
    titleAccent: 'Beauty Products',
    subtitle: 'Discover premium beauty essentials and trending skincare products',
    badge: 'NEW ARRIVALS',
    badgeIcon: 'ri-sparkling-2-line',
    accentColor: 'from-orange-400 via-pink-500 to-rose-500',
  },
  {
    image: 'https://readdy.ai/api/search-image?query=Modern%20smart%20health%20technology%20gadgets%20fitness%20trackers%20smartwatches%20and%20wellness%20devices%20displayed%20on%20minimalist%20white%20background%20with%20vibrant%20orange%20and%20pink%20accent%20lighting%20showcasing%20innovative%20health%20tech%20products&width=1920&height=1080&seq=hero-health-tech-slide2&orientation=landscape',
    title: 'Smart Health',
    titleAccent: 'Technology',
    subtitle: 'Advanced gadgets for your wellness and fitness journey',
    badge: 'TRENDING',
    badgeIcon: 'ri-pulse-line',
    accentColor: 'from-amber-400 via-orange-500 to-red-500',
  },
  {
    image: 'https://readdy.ai/api/search-image?query=Stylish%20modern%20kitchen%20accessories%20and%20gadgets%20including%20sleek%20utensils%20cookware%20and%20innovative%20kitchen%20tools%20arranged%20beautifully%20on%20clean%20white%20surface%20with%20warm%20coral%20lighting%20professional%20product%20photography&width=1920&height=1080&seq=hero-kitchen-slide3&orientation=landscape',
    title: 'Premium Kitchen',
    titleAccent: 'Essentials',
    subtitle: 'Quality kitchen accessories for modern living',
    badge: 'BEST SELLERS',
    badgeIcon: 'ri-award-line',
    accentColor: 'from-rose-400 via-pink-500 to-fuchsia-500',
  },
];

export default function Hero() {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      changeSlide((prev: number) => (prev + 1) % slides.length);
    }, 5500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePos({
          x: ((e.clientX - rect.left) / rect.width - 0.5) * 20,
          y: ((e.clientY - rect.top) / rect.height - 0.5) * 10,
        });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const changeSlide = (updater: (prev: number) => number) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide(updater);
      setIsTransitioning(false);
    }, 400);
  };

  const goToSlide = (index: number) => {
    if (index !== currentSlide) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentSlide(index);
        setIsTransitioning(false);
      }, 400);
    }
  };

  const slide = slides[currentSlide];

  return (
    <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
      {/* Background Slides */}
      {slides.map((s, index) => (
        <div
          key={index}
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-1000"
          style={{
            backgroundImage: `url(${s.image})`,
            opacity: currentSlide === index ? 1 : 0,
            transform: currentSlide === index
              ? `scale(1.05) translate(${mousePos.x * 0.3}px, ${mousePos.y * 0.3}px)`
              : 'scale(1)',
            transition: 'opacity 1s ease, transform 0.1s ease',
          }}
        />
      ))}

      {/* Multi-layer gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/60" />
      <div className={`absolute inset-0 bg-gradient-to-r opacity-40 transition-all duration-1000 ${slide.accentColor}`} />

      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((p) => (
          <div
            key={p.id}
            className="absolute rounded-full opacity-70"
            style={{
              width: p.size,
              height: p.size,
              left: `${p.left}%`,
              bottom: '-20px',
              backgroundColor: p.color,
              animation: `particle-float ${p.duration}s ${p.delay}s ease-in infinite`,
              boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
            }}
          />
        ))}
      </div>

      {/* Decorative spinning rings */}
      <div className="absolute top-1/4 right-10 w-64 h-64 pointer-events-none opacity-20 hidden md:block">
        <div className="absolute inset-0 rounded-full border-2 border-orange-400 animate-spin-slow" />
        <div className="absolute inset-4 rounded-full border-2 border-pink-400 animate-spin-reverse" />
        <div className="absolute inset-8 rounded-full border-2 border-amber-400 animate-spin-slow" />
      </div>
      <div className="absolute bottom-1/4 left-10 w-40 h-40 pointer-events-none opacity-15 hidden md:block">
        <div className="absolute inset-0 rounded-full border-2 border-rose-400 animate-spin-reverse" />
        <div className="absolute inset-3 rounded-full border-2 border-orange-300 animate-spin-slow" />
      </div>

      {/* Floating orbs */}
      <div
        className="absolute top-1/3 left-1/4 w-32 h-32 rounded-full opacity-20 pointer-events-none animate-float"
        style={{ background: 'radial-gradient(circle, #f97316, transparent)', filter: 'blur(20px)' }}
      />
      <div
        className="absolute bottom-1/3 right-1/4 w-48 h-48 rounded-full opacity-15 pointer-events-none animate-float-reverse"
        style={{ background: 'radial-gradient(circle, #ec4899, transparent)', filter: 'blur(30px)' }}
      />

      {/* Main Content */}
      <div
        className={`relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 text-center transition-all duration-700 pt-8 sm:pt-12 md:pt-16 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
        }`}
      >
        {/* Badge */}
        <div
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-white/30 text-white text-xs sm:text-sm font-semibold mb-6 mt-2 sm:mt-0 transition-all duration-500 ${
            isTransitioning ? 'opacity-0 scale-90' : 'opacity-100 scale-100'
          }`}
        >
          <i className={`${slide.badgeIcon} animate-heartbeat`} style={{ color: '#f97316' }}></i>
          <span className="tracking-widest truncate max-w-[120px] sm:max-w-none">{slide.badge}</span>
          <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse flex-shrink-0" />
        </div>

        {/* Main Title */}
        <div
          className={`transition-all duration-500 ${isTransitioning ? 'opacity-0 translate-y-8' : 'opacity-100 translate-y-0'}`}
        >
          <h1 className="text-4xl sm:text-6xl md:text-8xl font-black text-white mb-3 leading-none tracking-tight drop-shadow-2xl">
            <span className="block">{slide.title}</span>
            <span
              className="block mt-2 gradient-text-fire"
              style={{ WebkitTextStroke: '1px transparent' }}
            >
              {slide.titleAccent}
            </span>
          </h1>
        </div>

        {/* Brand name */}
        <div className="flex items-center justify-center gap-3 my-4">
          <div className="h-px w-10 sm:w-16 bg-gradient-to-r from-transparent to-orange-400" />
          <span className="text-orange-300 font-bold tracking-[0.3em] sm:tracking-[0.4em] text-xs sm:text-sm uppercase">AISHOPIO</span>
          <div className="h-px w-10 sm:w-16 bg-gradient-to-l from-transparent to-orange-400" />
        </div>

        {/* Subtitle */}
        <p
          className={`text-base sm:text-xl md:text-2xl text-white/90 mb-8 sm:mb-10 max-w-2xl mx-auto font-light leading-relaxed transition-all duration-500 delay-100 px-2 ${
            isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
          }`}
        >
          {slide.subtitle}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10 sm:mb-12 px-4">
          <button
            onClick={() => {
              const el = document.querySelector('[data-product-shop]');
              el?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="group relative w-full sm:w-auto px-8 sm:px-10 py-4 rounded-full font-bold text-white overflow-hidden transition-all duration-300 hover:scale-110 whitespace-nowrap cursor-pointer animate-neon-pulse"
            style={{ background: 'linear-gradient(135deg, #f97316, #ec4899, #f59e0b)', backgroundSize: '200% 200%' }}
          >
            <span className="relative z-10 flex items-center justify-center gap-2 text-base sm:text-lg">
              <i className="ri-shopping-bag-3-line"></i>
              Shop Now
              <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform duration-300"></i>
            </span>
            <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </button>

          <button
            onClick={() => navigate('/shop')}
            className="group relative w-full sm:w-auto px-8 sm:px-10 py-4 rounded-full font-bold text-white overflow-hidden transition-all duration-300 hover:scale-110 whitespace-nowrap cursor-pointer glass border-2 border-white/40 hover:border-orange-400/70"
          >
            <span className="relative z-10 flex items-center justify-center gap-2 text-base sm:text-lg">
              <i className="ri-play-circle-line group-hover:animate-heartbeat"></i>
              Explore More
            </span>
          </button>
        </div>

        {/* Stats bar */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-6 sm:mb-10 px-4 w-full">
          {[
            { icon: 'ri-user-smile-line', value: '10K+', label: 'Happy Customers' },
            { icon: 'ri-store-2-line', value: '500+', label: 'Products' },
            { icon: 'ri-star-fill', value: '4.9★', label: 'Rating' },
            { icon: 'ri-truck-line', value: 'Free', label: 'Shipping' },
          ].map((stat, i) => (
            <div
              key={i}
              className="glass rounded-2xl px-2 sm:px-5 py-1.5 sm:py-3 flex items-center gap-1.5 sm:gap-3 border border-white/20 hover:border-orange-400/50 transition-all duration-300 hover:scale-105 cursor-default"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-pink-600 flex-shrink-0">
                <i className={`${stat.icon} text-white text-xs`}></i>
              </div>
              <div className="text-left">
                <div className="text-white font-bold text-xs leading-none">{stat.value}</div>
                <div className="text-white/60 text-xs hidden sm:block">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Slide indicators */}
        <div className="flex gap-2 sm:gap-3 justify-center items-center w-full px-4 overflow-hidden flex-wrap pb-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`rounded-full transition-all duration-500 cursor-pointer flex-shrink-0 ${
                currentSlide === index
                  ? 'w-8 sm:w-14 h-3 neon-orange'
                  : 'w-3 h-3 bg-white/40 hover:bg-white/70'
              }`}
              style={currentSlide === index ? { background: 'linear-gradient(90deg, #f97316, #ec4899)' } : {}}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>


    </section>
  );
}
